<template>
  <div class="p-4">
    <!-- Header -->
    <div class="mb-4">
      <h1 class="text-xl font-bold text-gray-900 dark:text-white mb-0.5">
        Dashboard de Cotizaciones
      </h1>
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Estadísticas y métricas de cotizaciones
      </p>
    </div>

    <!-- Loading State -->
    <LoadingSpinner v-if="loading" center class="py-12" />

    <div v-else>
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <!-- Total Cotizaciones -->
        <BaseCard padding="compact" variant="default">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Cotizaciones</p>
              <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ stats.total }}</p>
            </div>
            <div class="w-10 h-10 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
          </div>
          <div class="mt-2 flex items-center gap-2">
            <BaseBadge variant="info" size="sm">{{ stats.pendientes }} Pendientes</BaseBadge>
          </div>
        </BaseCard>

        <!-- Aprobadas -->
        <BaseCard padding="compact" variant="default">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Aprobadas</p>
              <p class="text-2xl font-bold text-green-600 dark:text-green-400">{{ stats.aprobadas }}</p>
            </div>
            <div class="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
          <div class="mt-2">
            <p class="text-[10px] text-gray-500 dark:text-gray-400">
              {{ ((stats.aprobadas / stats.total) * 100).toFixed(1) }}% del total
            </p>
          </div>
        </BaseCard>

        <!-- Total Monto -->
        <BaseCard padding="compact" variant="default">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Monto Total</p>
              <p class="text-xl font-bold text-blue-600 dark:text-blue-400">${{ formatCurrency(stats.totalMonto) }}</p>
            </div>
            <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
          <div class="mt-2">
            <p class="text-[10px] text-gray-500 dark:text-gray-400">
              Promedio: ${{ formatCurrency(stats.montoPromedio) }}
            </p>
          </div>
        </BaseCard>

        <!-- Rechazadas -->
        <BaseCard padding="compact" variant="default">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Rechazadas</p>
              <p class="text-2xl font-bold text-red-600 dark:text-red-400">{{ stats.rechazadas }}</p>
            </div>
            <div class="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
          <div class="mt-2">
            <p class="text-[10px] text-gray-500 dark:text-gray-400">
              {{ ((stats.rechazadas / stats.total) * 100).toFixed(1) }}% del total
            </p>
          </div>
        </BaseCard>
      </div>

      <!-- Charts Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <!-- Cotizaciones por Día -->
        <BaseCard padding="compact" variant="default">
          <template #header>
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
              Cotizaciones por Día (Última Semana)
            </h3>
          </template>
          <div class="h-40">
            <BarChart
              :data="cotizacionesPorDiaData"
              barColor="bg-violet-500 dark:bg-violet-600"
              :chart-height="140"
            />
          </div>
        </BaseCard>

        <!-- Tendencia Semanal -->
        <BaseCard padding="compact" variant="default">
          <template #header>
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
              Tendencia Semanal (Montos)
            </h3>
          </template>
          <div class="h-40">
            <BarChart
              :data="tendenciaSemanalData"
              barColor="bg-blue-500 dark:bg-blue-600"
              :chart-height="140"
              :show-currency="true"
            />
          </div>
        </BaseCard>
      </div>

      <!-- Tables Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <!-- Prototipos Más Vendidos -->
        <BaseCard padding="compact" variant="default">
          <template #header>
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
              Top 5 Prototipos Más Cotizados
            </h3>
          </template>
          <div class="overflow-hidden">
            <table class="w-full text-xs">
              <thead class="text-[10px] uppercase bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th class="px-2 py-1.5 text-left font-bold text-gray-700 dark:text-gray-300">#</th>
                  <th class="px-2 py-1.5 text-left font-bold text-gray-700 dark:text-gray-300">Prototipo</th>
                  <th class="px-2 py-1.5 text-right font-bold text-gray-700 dark:text-gray-300">Cant.</th>
                  <th class="px-2 py-1.5 text-right font-bold text-gray-700 dark:text-gray-300">Monto</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(prototipo, index) in stats.prototiposMasVendidos"
                  :key="index"
                  class="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td class="px-2 py-1.5">
                    <BaseBadge :variant="getBadgeVariant(index)" size="sm">{{ index + 1 }}</BaseBadge>
                  </td>
                  <td class="px-2 py-1.5 text-gray-900 dark:text-white font-medium">{{ prototipo.nombre }}</td>
                  <td class="px-2 py-1.5 text-right text-gray-700 dark:text-gray-300">{{ prototipo.cantidad }}</td>
                  <td class="px-2 py-1.5 text-right text-gray-700 dark:text-gray-300 font-medium">${{ formatCurrency(prototipo.monto) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </BaseCard>

        <!-- Clientes Top -->
        <BaseCard padding="compact" variant="default">
          <template #header>
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
              Top Clientes
            </h3>
          </template>
          <div class="overflow-hidden">
            <table class="w-full text-xs">
              <thead class="text-[10px] uppercase bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th class="px-2 py-1.5 text-left font-bold text-gray-700 dark:text-gray-300">Cliente</th>
                  <th class="px-2 py-1.5 text-right font-bold text-gray-700 dark:text-gray-300">Cotiz.</th>
                  <th class="px-2 py-1.5 text-right font-bold text-gray-700 dark:text-gray-300">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(cliente, index) in stats.clientesTop"
                  :key="index"
                  class="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td class="px-2 py-1.5 text-gray-900 dark:text-white font-medium">
                    <div class="flex items-center gap-2">
                      <div class="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center text-white text-[10px] font-bold">
                        {{ cliente.nombre.charAt(0) }}
                      </div>
                      {{ cliente.nombre }}
                    </div>
                  </td>
                  <td class="px-2 py-1.5 text-right text-gray-700 dark:text-gray-300">
                    <BaseBadge variant="info" size="sm">{{ cliente.cotizaciones }}</BaseBadge>
                  </td>
                  <td class="px-2 py-1.5 text-right text-gray-700 dark:text-gray-300 font-medium">${{ formatCurrency(cliente.monto) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </BaseCard>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useCotizacionesStats } from '@/composables/useCotizacionesStats'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import LoadingSpinner from '@/components/base/LoadingSpinner.vue'
import BarChart from '@/components/charts/BarChart.vue'

const { stats, fetchStats, loading } = useCotizacionesStats()

onMounted(() => {
  fetchStats()
})

const cotizacionesPorDiaData = computed(() => {
  return stats.value.cotizacionesPorDia.map(item => ({
    label: new Date(item.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }),
    value: item.cantidad
  }))
})

const tendenciaSemanalData = computed(() => {
  return stats.value.tendenciaSemanal.map(item => ({
    label: item.semana,
    value: item.monto
  }))
})

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const getBadgeVariant = (index) => {
  const variants = ['primary', 'success', 'info', 'warning', 'neutral']
  return variants[index] || 'neutral'
}
</script>
